import React, { Fragment, MouseEvent } from 'react';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';

import {
  withStyles,
  WithStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';

const styles = (theme: Theme) => createStyles({
  item: {
    height: 44,
    padding: 0,
    backgroundColor: '#F0F1F4',
    position: 'relative',
    [theme.breakpoints.only('xs')]: {
      height: 40,
    },
    [theme.breakpoints.only('sm')]: {
      height: 42,
    },
    appearance: 'none',
    '&:click > a': {
      backgroundColor: theme.palette.primary.main,
    },
    '&:hover': {
      borderRadius: '10px 0 0 10px',
      backgroundColor: 'rgba(76, 104, 147, 0.12)',
      '&::after': {
        content: '""',
        position: 'absolute',
        right: 0,
        height: '19px',
        width: '19px',
        backgroundColor: 'inherit',
        bottom: '-18.6px',
        clipPath: 'path("M5.793 1.64a16 16 0 0 1 12.181 12.47L19 19.237V.368H.132l5.662 1.273Z")',
        zIndex: 8,
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        right: 0,
        height: '19px',
        width: '19px',
        backgroundColor: 'inherit',
        top: '-18.6px',
        clipPath: 'path("M5.793 18.096a16 16 0 0 0 12.181-12.47L19 .5v18.868H.132l5.662-1.272Z")',
        zIndex: 8,
      },
    },
  },
  active: {
    backgroundColor: '#FFF !important',
    borderRadius: '10px 0 0 10px',
    zIndex: 15,
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      height: '19px',
      width: '19px',
      backgroundColor: 'inherit',
      bottom: '-18.6px',
      clipPath: 'path("M5.793 1.64a16 16 0 0 1 12.181 12.47L19 19.237V.368H.132l5.662 1.273Z")',
      zIndex: 10,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      right: 0,
      height: '19px',
      width: '19px',
      backgroundColor: 'inherit',
      top: '-18.6px',
      clipPath: 'path("M5.793 18.096a16 16 0 0 0 12.181-12.47L19 .5v18.868H.132l5.662-1.272Z")',
      zIndex: 10,
    },
  },

  text: {
    color: '#323232',
    fontSize: '16px',
    lineHeight: '20px',
  },
  subMenuItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 6px',
    margin: '0 0 8px 0',
    borderRadius: 4,
    height: 48,
    [theme.breakpoints.only('xs')]: {
      height: 44,
    },
    [theme.breakpoints.only('sm')]: {
      height: 46,
    },
  },
  subMenuText: {
    paddingLeft: 50,
    fontSize: 14,
  },
  wrapList: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 0,
  },
  listItemContainer: {
    display: 'flex',
    padding: 12,
    width: '100%',
    height: '100%',
    zIndex: 2,
    [theme.breakpoints.only('xs')]: {
      padding: 10,
    },
    [theme.breakpoints.only('sm')]: {
      padding: 11,
    },

  },
  textWrapper: {
    marginTop: 0,
    marginBottom: 0,
  }
});

interface IProps extends WithStyles<typeof styles> {
  text: string;
  icon: React.ReactElement;
  route: string;
  pathname: string;
  subMenu?: Array<{ name: string; route: string; }>;
  showSubMenu?: boolean;
  handleSwitchSubMenu?: (event: MouseEvent<HTMLButtonElement>) => void;
  handleSwitchDrawerClose?: () => void;
  disabled?: boolean;
  isActive?: boolean;
}

const SideBarItem: React.FC<IProps> = ({
  classes,
  text,
  icon,
  route,
  pathname,
  subMenu,
  showSubMenu,
  handleSwitchSubMenu,
  handleSwitchDrawerClose,
  disabled,
  isActive
}) => {

  const subMenuList = showSubMenu && (
    <Collapse
      className={classes.wrapList}
      in={showSubMenu}
      timeout="auto"
      unmountOnExit
    >
      <List
        component="div"
        disablePadding
      >
        {subMenu && subMenu.map(element => (
          <ListItem
            disabled={disabled}
            className={classNames(classes.subMenuItem, {
              [classes.active]: pathname.startsWith(element.route),
            })}
            onClick={handleSwitchDrawerClose}
            // @ts-ignore
            component={NavLink}
            to={element.route}
            key={element.name}
            button
          >
            <ListItemText
              primary={(
                <Typography
                  className={classNames(classes.text, classes.subMenuText)}
                >
                  {element.name}
                </Typography>
              )}
            />
          </ListItem>
        ))}
      </List>
    </Collapse>
  );

  return (
    <Fragment>
      <ListItem
        disabled={disabled}
        className={classNames(classes.item, {
          [classes.active]: isActive,
        })}
        component={NavLink}
        onClick={handleSwitchDrawerClose}
        to={route}
        button
      >
        <div className={classes.listItemContainer}>
          <ListItemIcon style={{ minWidth: 20, paddingRight: 10 }}>
            {icon}
          </ListItemIcon>
          <ListItemText className={classes.textWrapper} primary={(
            <Typography
              variant="body1"
              className={classes.text}
            >
              {text}
            </Typography>
          )}
          />

          {subMenu && (
            <IconButton
              className={classes.text}
              onClick={handleSwitchSubMenu}
            >
              {showSubMenu ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </div>
      </ListItem>
      {subMenuList}
    </Fragment>
  );
};

export default withStyles(styles)(SideBarItem);
